import {
  Component, Input, Output, EventEmitter,
  OnInit, OnDestroy, ChangeDetectorRef, HostListener,
  ElementRef, Renderer2
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { routeAlias } from '../../../../routesalias';
import { AuthService } from '../../../services/auth/auth.service';
import { closeLocalStorage } from '../../../utils/helpers';
import { NgIf, NgFor, NgClass } from '@angular/common';
import { ClickOutsideDirective } from '../../../directives/click-outside.directive';
import { FormsModule } from '@angular/forms';
import { SettingService } from '../../../services/setting/setting.service';
import { AppService } from '../../../core/app.service';

export interface MenuItemProps {
  label: string;
  link?: string;
  icon?: string;
  roles?: string[];
  action?: 'logout' | 'link';
  badge?: string;
  children?: MenuItemProps[];
  _open?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, MatIconModule, NgIf, NgFor, NgClass, ClickOutsideDirective, FormsModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
})
export class SidebarComponent implements OnInit {

  readonly routeAlias = routeAlias;

  @Input() collapsed = false;
  @Input() disableClickOutside = false;

  mobileOpen = false;
  footerDropdownOpen = false;

  @Output() oneMenuIsClicked = new EventEmitter<void>();
  @Output() clickOutside = new EventEmitter<void>();
  @Output() collapsedChange = new EventEmitter<boolean>();

  menuItems: MenuItemProps[] = [
    { label: 'Tableau de bord', link: routeAlias.home, icon: 'dashboard' },
    {
      label: 'Planification', icon: 'track_changes',
      children: [
        /* { label: 'Tableau de bord', icon: 'dashboard' }, */
        { label: 'Activités', link: routeAlias.activities.home, icon: 'timeline', roles: ['RA'] },
        { label: 'Budgets', link: routeAlias.budgets.home, icon: 'account_balance_wallet', roles: ['PRMP'] },
        { label: 'Marchés', link: routeAlias.marches.home, icon: 'sell', roles: ['PRMP', 'RA'] },
      ],
    },
    {
      label: 'Execution', icon: 'folder_special', badge: 'En cours', roles: ['dev'],
      children: [
        { label: 'Tableau de bord', icon: 'dashboard' },
        {
          label: 'Rapports', icon: 'description',
          children: [
            { label: 'Codire', link: `${routeAlias.rapports.home}/${routeAlias.rapports.codir}`, icon: 'calendar_month', roles: ['dev'] },
            { label: 'Rapports mensuels', link: `${routeAlias.rapports.home}/${routeAlias.rapports.rapport_mensuel}`, icon: 'calendar_month', roles: ['dev'] },
            { label: 'Rapports Trimestriels', link: `${routeAlias.rapports.home}/${routeAlias.rapports.rapport_trimestriel}`, icon: 'calendar_month', roles: ['dev'] },
            { label: 'Rapports annuels', link: `${routeAlias.rapports.home}/${routeAlias.rapports.rapport_annuel}`, icon: 'bar_chart', roles: ['dev'] },
          ],
        },
        { label: 'Décisions', icon: 'gavel' },
        { label: 'Membres', icon: 'people' },
      ],
    },
    {
      label: 'Suivi & Evaluation', icon: 'folder_special', badge: 'En cours', roles: ['dev'],
      children: [],
    }
  ];

  footerMenuItems: MenuItemProps[] = [
    { label: 'Paramètres', link: routeAlias.parameters.home, icon: 'settings', roles: ['SU', 'PRMP', 'COMPTABLE'], action: 'link' },
    { label: 'Administration', link: routeAlias.administration.home, icon: 'admin_panel_settings', roles: ['ADMIN'], action: 'link' },
    { label: 'Système', link: routeAlias.setting.home, icon: 'tune', roles: ['ADMIN'], action: 'link' },
  ];

  brandSigle: string | null = null;
  brandLogoFull: string | null = null;
  private logoCacheBust = Date.now();

  user: { name?: string; role?: string } | null = null;
  @Input() backgroundColor?: string;

  private isMobile = false;

  constructor(
    private authService: AuthService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private el: ElementRef,
    private renderer: Renderer2,
    private appService: AppService,
    private settingService: SettingService
  ) { }

  ngOnInit(): void {
    this.checkViewport();
    try {
      const info = this.authService.getUserInfo();
      if (info) this.user = {
        name: info.username || info.preferred_username || '',
        role: info.roles?.[0] || ''
      };
    } catch { }
    this.autoOpenActiveGroup(this.menuItems);

    const appId = this.appService.getAppId();
    this.settingService.getSystemBrand(appId).subscribe((data) => {
      if (!data) return;
      for (const d of data.listeDetailsParametres ?? []) {
        if (d.code === 'SIGLE') this.brandSigle = d.valeurAN;
        if (d.code === 'LOGO_PATH') this.brandLogoFull = d.valeurAN;
      }
      this.logoCacheBust = Date.now();
      this.cdr.markForCheck();
    });

  }

  @HostListener('window:resize')
  onResize(): void { this.checkViewport(); }

  private checkViewport(): void {
    this.isMobile = window.innerWidth <= 768;
    if (!this.isMobile && this.mobileOpen) { this.mobileOpen = false; }
    this.cdr.markForCheck();
  }

  openMobile(): void {
    this.mobileOpen = true;
    this.renderer.addClass(this.el.nativeElement, 'mobile-open');
    this.cdr.markForCheck();
  }

  closeMobile(): void {
    this.mobileOpen = false;
    this.renderer.removeClass(this.el.nativeElement, 'mobile-open');
    this.cdr.markForCheck();
  }

  toggleMobile(): void { this.mobileOpen ? this.closeMobile() : this.openMobile(); }

  toggleCollapse(): void {
    this.collapsed = !this.collapsed;
    if (this.collapsed) { this.closeAllGroups(this.menuItems); this.footerDropdownOpen = false; }
    this.collapsedChange.emit(this.collapsed);
    this.cdr.markForCheck();
  }

  get brandLogoUrl(): string | null {
    const base = this.settingService.buildLogoUrl(this.brandLogoFull);
    return base ? `${base}?t=${this.logoCacheBust}` : null;
  }

  private closeAllGroups(items: MenuItemProps[]): void {
    for (const item of items) {
      item._open = false;
      if (item.children) this.closeAllGroups(item.children);
    }
  }

  toggleGroup(item: MenuItemProps, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    item._open = !item._open;
    this.cdr.markForCheck();
  }

  hasChildren(item: MenuItemProps): boolean { return !!(item.children?.length); }

  private autoOpenActiveGroup(items: MenuItemProps[]): boolean {
    let found = false;
    for (const item of items) {
      if (item.children) {
        if (this.autoOpenActiveGroup(item.children)) { item._open = true; found = true; }
      } else if (item.link && this.isLinkActive(item.link)) { found = true; }
    }
    return found;
  }

  isLinkActive(link?: string): boolean {
    if (!link) return false;
    try {
      const url = this.router.url || '';
      return url === link || url.startsWith(link + '/');
    } catch { return false; }
  }

  isActive(item: MenuItemProps): boolean { return this.isLinkActive(item.link); }

  hasActiveChild(item: MenuItemProps): boolean {
    if (!item.children) return false;
    return item.children.some(c => this.isLinkActive(c.link) || this.hasActiveChild(c));
  }

  onClickMenu(): void {
    try { closeLocalStorage(); } catch { }
    this.oneMenuIsClicked.emit();
    if (this.isMobile) this.closeMobile();
  }

  handleClickOutside(): void {
    if (!this.disableClickOutside) {
      this.clickOutside.emit();
      if (this.isMobile) this.closeMobile();
    }
  }

  toggleFooterDropdown(event: Event): void {
    event.stopPropagation();
    this.footerDropdownOpen = !this.footerDropdownOpen;
    this.cdr.markForCheck();
  }

  closeFooterDropdown(): void {
    if (!this.footerDropdownOpen) return;
    this.footerDropdownOpen = false;
    this.cdr.markForCheck();
  }

  logout(): void { try { this.authService.logout(); } catch { } }

  hasAccess(roles?: string[]): boolean {
    return !roles?.length || this.authService.hasAnyRole(roles);
  }
}